import * as React from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { Breadcrumb, Tag, Select, Spin } from "antd";
import { Modal } from "Components/General/Modal/GenericModal";
import { ColumnProps } from "antd/lib/table";
import { dayjs } from "Config/dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import { observer } from "mobx-react";
import { breadcrumbsStore as breadcrumbs } from "../Stores/BreadcrumbsStore";
import { withTranslation, WithTranslation } from "react-i18next";
import { stringSort, dateSort, numberSort } from "utils/defaultSort";
import { TableActionsDropdown } from "./TableActionsDropdown";
import { API } from "General/API";
import { projectsDataStore } from "Stores/ProjectsStore";
import { FileAnalyze } from "./FileAnalyze";
import { ActionButtons, ActionButton } from "Components/Layout/ActionButtons";
import { notificationError } from "utils/libHelpers/AntdNotification";
import { I18N_NS } from "Config/i18n";
import { API_URL } from "General/APIEndpoints";
import { withRouter, WithRouterProps } from "Components/General/WithRouter";
import { Table } from "Components/General/Table";

export type actionType = "move" | "analyze";

interface AIDocumentsTableProps extends WithRouterProps, WithTranslation {
  matchUrl: string;
}

interface AIDocumentsTableState {
  data: Array<API_CategoryFileDataEntity>;
  categories: { [categoryId: number]: string };
  loading: boolean;
  currentFileId: number;
  currentAction: actionType;
}

class AIDocumentsTableColumn extends React.Component<ColumnProps<API_CategoryFileDataEntity>> {}

const TypeIcon = ({ type }) => (
  <FontAwesomeIcon fixedWidth={true} icon={["far", type === "FOLDER" ? "folder" : "file-alt"]} />
);

export const AIDocumentsTable = withTranslation(I18N_NS.PROJECT)(
  withRouter(
    observer(
      class extends React.Component<AIDocumentsTableProps, AIDocumentsTableState> {
        constructor(props: AIDocumentsTableProps) {
          super(props);

          this.state = {
            data: [],
            categories: {},
            loading: false,
            currentFileId: undefined,
            currentAction: undefined,
          };
        }

        componentDidMount() {
          (async () => await this.handleFetchData())();
        }

        componentDidUpdate(prevProps: AIDocumentsTableProps) {
          if (prevProps.params.folderId !== this.props.params.folderId) {
            (async () => await this.handleFetchData())();
          }
        }

        async handleFetchData() {
          const { folderId } = this.props.params;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { state } = this.props.location as any;
          const breadcrumbName = state && state.breadcrumbName ? state.breadcrumbName : `FOLDER ${folderId}`;
          try {
            this.setState({ loading: true });
            let response;
            if (folderId) {
              response = await API.getData(API_URL.CATEGORIES.getData(projectsDataStore.projectId, +folderId));
            } else {
              response = await API.getData(API_URL.CATEGORIES.getRoot(projectsDataStore.projectId));
            }
            const categories = await API.getData(API_URL.CATEGORIES.getCategories(projectsDataStore.projectId));
            this.setState({ data: response, categories });
          } catch (err) {
            throw err;
          } finally {
            breadcrumbs.changeCrumbs({ title: breadcrumbName, folderId: folderId && +folderId });
            this.setState({ loading: false });
          }
        }

        closeModal = () => {
          this.setState({ currentFileId: undefined, currentAction: undefined });
        };

        handleFetchFolder = async (record: API_CategoryFileDataEntity) => {
          if (record.type === "FOLDER") {
            // > -2 - That's because Uncategorized folder has always id -1, Unsure folder has id 0.
            // If it's lower - redirect to root folder.
            // Not sure if it handles anything but with current back-end I'd rather catch it instead of fix it.
            this.props.navigate(`${this.props.matchUrl}/${record.id > -2 ? record.id : ""}`, {
              // Pass folder name to location state in order to show it in the breadcrumbs
              state: { breadcrumbName: record.name },
            });
          }
        };

        selectAction = (id: number, type: actionType) => {
          this.setState({ currentFileId: id, currentAction: type });
        };

        handleMoveFile = async (catId: string) => {
          try {
            const { currentFileId } = this.state;
            await API.editData(
              API_URL.CATEGORIES.updateCategory(projectsDataStore.projectId, Number(catId), currentFileId),
              {}
            );
            await this.handleFetchData();
          } catch (err) {
            notificationError(this.props.t("Error"), err.message);
            throw err;
          }
        };

        categoryNames = (categoryId: number[]) => {
          const { categories } = this.state;
          const categoriesTags = categoryId.map((id) => (
            <Tag style={{ cursor: "default" }} key={id}>
              {categories[id]}
            </Tag>
          ));
          return categoriesTags;
        };

        backToDocuments = () => {
          const { navigate } = this.props;
          navigate(`/project/${projectsDataStore.projectId}/documents/docs`);
          // history.push(`${matchUrl}`.substring(0, matchUrl.indexOf('/ai')));
        };

        certaintySort = (first: API_CategoryFileDataEntity, second: API_CategoryFileDataEntity) => {
          if (!first.parentId) {
            return 1;
          }
          const a = Object.values(first) && Object.values(first)[0];
          const b = Object.values(second) && Object.values(second)[0];
          return numberSort(a, b);
        };

        render() {
          const { params, matchUrl, t } = this.props;
          const { categories, currentFileId, data, currentAction } = this.state;
          const currentFile = data.find((item) => item.id === currentFileId);
          const currentFileCategoryId =
            currentFile && currentFile.certainties ? Object.keys(currentFile.certainties)[0] : 0;

          return (
            <div>
              <ActionButtons>
                <ActionButton
                  icon="arrow-circle-left"
                  label={"Back to normal view"}
                  clickAction={this.backToDocuments}
                />
              </ActionButtons>
              <Breadcrumb separator=">" style={{ marginBottom: 10 }}>
                <Breadcrumb.Item key="root">
                  {params.folderId ? (
                    <Link to={`${matchUrl}`}>{projectsDataStore.currentProject.name}</Link>
                  ) : (
                    projectsDataStore.currentProject.name
                  )}
                </Breadcrumb.Item>
                {breadcrumbs.data.map((item) =>
                  item.folderId === +params.folderId ? (
                    <Breadcrumb.Item key={item.title}>{item.title}</Breadcrumb.Item>
                  ) : (
                    <Breadcrumb.Item key={item.title}>
                      <Link to={`${matchUrl}/${item.folderId > -1 ? item.folderId : ""}`}>{item.title}</Link>
                    </Breadcrumb.Item>
                  )
                )}
              </Breadcrumb>
              <Spin spinning={this.state.loading} indicator={<LoadingOutlined style={{ fontSize: 24 }} spin={true} />}>
                <Table
                  name="aiDocs"
                  scroll={{ x: 1000 }}
                  rowKey={"id"}
                  size="small"
                  dataSource={this.state.data}
                  onRow={(record: API_CategoryFileDataEntity) => ({
                    onClick: () => {
                      this.handleFetchFolder(record);
                    },
                  })}
                  pagination={{ defaultPageSize: 20 }}
                >
                  <AIDocumentsTableColumn
                    align="center"
                    width={50}
                    dataIndex="type"
                    render={(text) => <TypeIcon type={text} />}
                  />
                  <AIDocumentsTableColumn
                    title={t("File name")}
                    dataIndex="name"
                    // FIXME: Why does it render on hover?
                    render={(text, rec) =>
                      rec.type === "FOLDER" && rec.categories ? (
                        <>
                          {text} <span style={{ marginLeft: 5 }}>{this.categoryNames(rec.categories)}</span>
                        </>
                      ) : (
                        text
                      )
                    }
                    sorter={(a, b) => stringSort(a, b, "name")}
                  />
                  {/* Uses only first value in array (temporary). Because back-end returns one prop. */}
                  <AIDocumentsTableColumn
                    title={t("Certainty")}
                    dataIndex="certainties"
                    align="center"
                    render={(text) => (text ? `${Object.values(text)[0]}` : null)}
                    width={150}
                    sorter={this.certaintySort}
                  />
                  <AIDocumentsTableColumn
                    title={t("Description")}
                    className="text-ellipsis"
                    dataIndex="description"
                    width={200}
                    sorter={(a, b) => stringSort(a, b, "description")}
                  />
                  <AIDocumentsTableColumn
                    title={t("Index")}
                    className="text-ellipsis"
                    dataIndex="index"
                    width={100}
                    sorter={(a, b) => stringSort(a, b, "index")}
                  />
                  <AIDocumentsTableColumn
                    title={t("Last change")}
                    dataIndex="created"
                    width={150}
                    defaultSortOrder="descend"
                    sorter={(a, b) => dateSort(a, b, "created")}
                    render={(text) => dayjs(text).format("ll")}
                  />
                  <AIDocumentsTableColumn
                    title={t("Size")}
                    dataIndex="size"
                    width={100}
                    sorter={(a, b) => stringSort(a, b, "size")}
                  />
                  <AIDocumentsTableColumn
                    align="center"
                    title={t("Actions")}
                    fixed="right"
                    dataIndex="id"
                    width={70}
                    render={(id, record) =>
                      record.type === "FOLDER" ? null : (
                        <TableActionsDropdown
                          hasCertainties={!!record.certainties}
                          easyViewer={record.viewers && record.viewers.easyViewer}
                          fileId={id}
                          fileName={record.name}
                          onClick={this.selectAction}
                        />
                      )
                    }
                  />
                </Table>
              </Spin>
              <Modal
                open={!!currentFileId}
                title={currentAction === "move" ? t("Move-to") : t("Analyze content")}
                footer={null}
                destroyOnHidden={true}
                onCancel={this.closeModal}
              >
                {currentAction === "move" && (
                  <Select
                    style={{ width: `100%` }}
                    onChange={this.handleMoveFile}
                    defaultValue={`${currentFileCategoryId}`}
                    options={
                      Object.keys(categories).length > 0
                        ? Object.keys(categories).map((id) => ({ value: `${id}`, label: categories[id] }))
                        : []
                    }
                  ></Select>
                )}
                {currentAction === "analyze" && <FileAnalyze fileId={currentFileId} />}
              </Modal>
            </div>
          );
        }
      }
    )
  )
);
